/**
 * Fight villain move for the Legendary Arena game engine.
 *
 * fightVillain removes a villain or henchman from a City space and places
 * it in the current player's victory pile. Follows the three-step
 * validation contract: validate args, check stage gate, mutate G.
 *
 * This is a non-core move that gates internally (same pattern as
 * revealVillainCard from WP-014A). It is NOT added to CoreMoveName,
 * CORE_MOVE_NAMES, or MOVE_ALLOWED_STAGES.
 *
 * No registry imports. No .reduce(). Moves never throw.
 */
import { formatCardRef } from '../log/logDisplay.js';
// why: WP-823 / D-24756 — a defeated Brainwash the Military Officer is gained as the
// fungible S.H.I.E.L.D. Officer token.
import { SHIELD_OFFICER_EXT_ID } from '../setup/pilesInit.js';
import { awardAttachedBystanders } from '../board/bystanders.logic.js';
import { awardAttachedHeroes } from '../board/heroCapture.logic.js';
import { getSpendableAttackForTarget, spendFightCostForTarget, markExcessiveViolenceUsed, spendRecruit, addResources } from '../economy/economy.logic.js';
import { UNNAMED_CITY_SPACE_TARGET } from '../economy/economy.types.js';
import { RESCUE_ON_FIGHT_RECRUIT_COST, canPayRescueOnFight, isRescueOnFightOffered, } from '../economy/rescueOnFight.logic.js';
import { moveCardFromZone } from './zoneOps.js';
import { citySpaceNameForIndex } from '../board/citySpaceNames.js';
// why: WP-736 / D-24556 — the fight-time driver that fires every enrolled Excessive Violence
// ability. Called from THIS move body (never the shared defeatCityVillainCore, which non-fight
// defeat paths reach without the useExcessiveViolence arg), after the defeat + the extra-attack debit.
import { fireExcessiveViolencePlays } from '../hero/heroEffects.execute.js';
import { resolveFightCost, resolveFightPrice } from '../economy/economy.resolve.js';
import { isGuardBlocking, getPatrolModifier } from '../board/boardKeywords.logic.js';
import { getDefeatRequirement, playerMeetsDefeatRequirement, getFightDiscardCost, isHandCardCostOneOf, } from './villainDefeatRequirement.logic.js';
// why: WP-817 / D-24750 — Primus's fight discard cost goes through the one card-effect
// hand→discard chokepoint, so return-on-discard and the discarded-this-turn counter fire.
import { discardFromHand } from './discardFromHand.js';
import { executeVillainAbilities, resolveEffectResultNames, villainCardPlaysVillainDeckCards, readTurnNumber, } from '../villain/villainEffects.execute.js';
import { playTopVillainDeckCards } from '../villainDeck/villainDeck.reveal.js';
// why: WP-542 / D-24351 — the STATIC handler map performVillainReveal needs. The fight move
// does not receive an implementationMap (only the reveal move + scheme resolvers thread one),
// so it imports the module constant — the same one game.ts + villainDeck.reveal.ts import.
import { DEFAULT_IMPLEMENTATION_MAP } from '../rules/ruleRuntime.impl.js';
import { hasPendingKoHeroChoice } from './koHeroChoice.resolve.js';
import { hasPendingScryKoChoice } from './scryKoChoice.resolve.js';
import { hasPendingMelterKoChoice } from './melterKoChoice.resolve.js';
import { hasPendingRuthlessDictatorChoice } from './ruthlessDictatorChoice.resolve.js';
import { hasPendingElectromagneticBubbleChoice } from './electromagneticBubbleChoice.resolve.js';
import { hasPendingDiscardChoice } from './discardChoice.resolve.js';
import { hasPendingPutCardsOnDeckChoice } from './putCardsOnDeckChoice.resolve.js';
import { hasPendingKoDiscardChoice } from './koDiscardChoice.resolve.js';
import { hasPendingReorderChoice } from './reorderChoice.resolve.js';
import { hasPendingDefeatChoice } from './defeatChoice.resolve.js';
import { hasPendingOptionalKoReward } from './optionalKoReward.resolve.js';
import { hasPendingSmashDiscard } from './smashDiscard.resolve.js';
import { hasPendingPutHandOnDeckTop } from './putHandOnDeckTop.resolve.js';
import { hasPendingRevealTopDispose } from './revealTopDispose.resolve.js';
import { hasPendingRevealThreeAssign } from './revealThreeAssign.resolve.js';
import { hasPendingDoOver } from './doOver.resolve.js';
import { hasPendingPlayVillainTopChoice } from './playVillainTop.resolve.js';
import { hasPendingVictoryPileCardPick } from './resolveVictoryPileCardPick.js';
import { hasPendingDrawOrEmpowered } from './drawOrEmpowered.resolve.js';
import { hasPendingCoveringFireChoice } from './coveringFireChoice.resolve.js';
import { hasPendingMoveVillainChoice } from './moveVillainChoice.resolve.js';
import { hasPendingSplitFaceChoice } from './splitFaceChoice.resolve.js';
import { hasPendingCountScaledChoice } from './countScaledChoice.resolve.js';
import { hasPendingUndercoverChoice } from './undercover.resolve.js';
import { hasPendingReturnZeroCostDiscard } from './resolveReturnZeroCostDiscard.js';
import { hasPendingDiscardToPlay } from './resolveDiscardToPlay.js';
import { hasPendingReturnOnDiscard } from './resolveReturnOnDiscard.js';
import { hasPendingGiveHqHeroChoice } from './giveHqHeroChoice.resolve.js';
import { hasPendingCopyPowersChoice } from './copyPowersChoice.resolve.js';
import { hasPendingSeatChoice } from './seatChoice.resolve.js';
import { hasHealedThisTurn } from './healWounds.js';
import { composeFightNarrative, composeEffectResultLogLine, } from '../events/notableEvents.compose.js';
import { pushLog } from '../log/logPush.js';
import { queueEnigmaPeekOffer } from '../rules/enigmaCode.logic.js';
/**
 * Fights a villain or henchman in the City.
 *
 * Removes the card from the specified City space and places it in the
 * current player's victory pile.
 *
 * @param context - boardgame.io move context with G, ctx.
 * @param args - The city space index to fight.
 */
export function fightVillain({ G, ...context }, { cityIndex, useExcessiveViolence, discardCardId, payToRescueBystander }) {
    // why: WP-754 / D-24581 — take the spread move context (the playCard shape) so the Excessive
    // Violence fire below hands hero handlers the context they expect, with `random` on it. Passing
    // bgio's bare `ctx` (no `random`) made any EV inner effect that reshuffles an empty deck (Rending
    // Claws' draw, Gruesome Feast's reveal) call `.random.Shuffle` on undefined and throw in a move.
    const ctx = context.ctx;
    const random = context.random;
    // Step 1: Validate args
    if (typeof cityIndex !== 'number' ||
        !Number.isFinite(cityIndex) ||
        !Number.isInteger(cityIndex) ||
        cityIndex < 0 ||
        // why: WP-831 / D-24764 — a space that exists in the current City (a scheme can resize it).
        cityIndex >= G.city.length) {
        return;
    }
    const cardId = G.city[cityIndex];
    if (cardId === null || cardId === undefined) {
        return;
    }
    // why: WP-790 / D-24652 — the fight's restricted-attack target is the named City space of
    // the fought index. WP-831 / D-24764 — a space beyond the normal five exists but has no name;
    // its target is UNNAMED_CITY_SPACE_TARGET, which no restricted grant names, so it is fought
    // with plain Attack only. (The index guard above already proved the space exists.)
    const fightTarget = citySpaceNameForIndex(cityIndex) ?? UNNAMED_CITY_SPACE_TARGET;
    // why: Guard blocks access to lower-index City cards. A Guard at a higher
    // index (closer to escape) prevents fighting villains behind it. You must
    // defeat the Guard first. Guard check uses defensive access (G.cardKeywords
    // may be undefined in pre-WP-025 test states).
    const cardKeywords = G.cardKeywords ?? {};
    if (isGuardBlocking(G.city, cityIndex, cardKeywords)) {
        return;
    }
    // why: Patrol adds +1 to the fight cost (MVP additive modifier). The
    // patrol modifier is additive on top of the resolved fight cost.
    // resolveFightCost is the single authority — handles both static and
    // dynamic (captured-hero-based) villains (WP-214).
    // why: WP-760 / D-24589 — pass the fighter so a Blood Frenzy villain's cost counts
    // THEIR Victory Pile (the same player the bot and the City projection pass).
    // why: D-24802 — the fight is paid at the Size-Changing price (rules v23 p.33 / p.30), read
    // from the fighter's cards played this turn; resolveFightCost stays the villain's Attack for
    // the log note. Microscopic Size-Changing can take the price below 0: the fighter pays
    // nothing for it and gains the difference as Attack after the debit (p.30).
    const villainAttack = resolveFightCost(G, cardId, ctx.currentPlayer);
    const fightPrice = resolveFightPrice(G, cardId, ctx.currentPlayer);
    const patrolModifier = getPatrolModifier(cardId, cardKeywords);
    const requiredFightCost = Math.max(0, fightPrice) + patrolModifier;
    // why: WP-580 / D-24389 — the spendable figure folds in unspent recruit when the
    // recruit-as-attack conversion is active this turn (God of Thunder); equal to
    // getAvailableAttack on every non-conversion turn, so the gate is unchanged there.
    // why: WP-790 / D-24652 — the gate is target-aware: restricted ("usable only against …")
    // attack counts only when its grant names this City space.
    const spendableAttack = getSpendableAttackForTarget(G.turnEconomy, fightTarget);
    if (spendableAttack < requiredFightCost) {
        return;
    }
    // why: D-24076 — defeat-requirement precondition; a marked villain
    // (Blob/Venom/Zombie Venom) can't be defeated unless the current player has a
    // qualifying Hero in hand or in play. Silent return on block, mirroring the
    // Guard-block gate above — no G mutation, no message, no event, no throw. The
    // two helpers are the single authority for this test (no inline matching).
    const defeatRequirement = getDefeatRequirement(G, cardId);
    if (defeatRequirement !== null &&
        !playerMeetsDefeatRequirement(G, ctx.currentPlayer, defeatRequirement)) {
        return;
    }
    // why: WP-817 / D-24750 — a fight discard cost is a D-24185 card-specific pre-commit
    // precondition, extended from a played card to a fought villain: the move argument
    // names the card, and the move refuses silently, before any G write, unless that card
    // is in hand and costs one of the listed costs. Paying it in the same move as the fight
    // keeps the two atomic, so no pending discard can be stranded by a later effect.
    const fightDiscardCost = getFightDiscardCost(G, cardId);
    if (fightDiscardCost !== null) {
        if (typeof discardCardId !== 'string' || discardCardId.length === 0) {
            return;
        }
        const currentHand = G.playerZones[ctx.currentPlayer]?.hand ?? [];
        if (!currentHand.includes(discardCardId)) {
            return;
        }
        if (!isHandCardCostOneOf(G, discardCardId, fightDiscardCost.eligibleCosts)) {
            return;
        }
    }
    // Step 2: Stage gate (non-core move, internal gating)
    // why: fighting happens during the main action window; non-core moves
    // gate internally per the WP-014A precedent
    if (G.currentStage !== 'main')
        return;
    // why: block-all guard (D-24008) — while a KO-a-Hero choice is pending the
    // board is frozen; fightVillain returns with no side effects so the player
    // resolves the Fight effect before fighting again. Placed immediately after
    // the stage gate, before any G/zone write.
    if (hasPendingKoHeroChoice(G))
        return;
    // why: block-all guard (D-24282) — a pending Doombot scry-KO choice freezes the
    // board until the player picks which revealed card to KO. (fightVillain is the
    // Doombot trigger path itself, so this guards a re-fight while the park is open.)
    if (hasPendingScryKoChoice(G))
        return;
    // why: WP-603 / D-24413 — block-all guard: a pending Melter Fight KO/keep choice
    // freezes the board until the fighting player resolves every revealed deck top.
    if (hasPendingMelterKoChoice(G))
        return;
    // why: WP-695 / D-24512 — block-all guards for the two interactive core mastermind
    // tactics (Ruthless Dictator scry-3 / Electromagnetic Bubble X-Men pick).
    if (hasPendingRuthlessDictatorChoice(G))
        return;
    if (hasPendingElectromagneticBubbleChoice(G))
        return;
    // why: block-all guard (WP-476 / D-24284) — a pending discard-to-limit choice
    // freezes the board until the current player picks which cards to discard.
    if (hasPendingDiscardChoice(G))
        return;
    if (hasPendingPutCardsOnDeckChoice(G))
        return;
    if (hasPendingReorderChoice(G))
        return; // why: WP-479 / D-24286 block-all guard
    if (hasPendingDefeatChoice(G))
        return; // why: WP-486 / D-24291 block-all guard
    if (hasPendingKoDiscardChoice(G))
        return; // why: WP-693 / D-24510 block-all guard
    // why: block-all guard (D-24019) — optional-KO-reward choice pending; the
    // board is frozen until resolved (beside the D-24008 KO-hero check above).
    if (hasPendingPlayVillainTopChoice(G))
        return; // why: WP-663 / D-24474 — block-all guard (Shadowed Thoughts play-villain-top choice)
    if (hasPendingOptionalKoReward(G))
        return;
    if (hasPendingSmashDiscard(G))
        return; // why: WP-676 / D-24492 — block-all guard (Smash discard-for-attack choice)
    if (hasPendingPutHandOnDeckTop(G))
        return; // why: WP-700 / D-24519 — block-all guard (put-a-hand-card-on-deck-top choice)
    if (hasPendingRevealTopDispose(G))
        return; // why: WP-702 / D-24521 — block-all guard (reveal-top discard-or-keep choice)
    if (hasPendingRevealThreeAssign(G))
        return; // why: WP-753 / D-24580 — block-all guard (reveal-three draw / discard / KO assignment)
    if (hasPendingDoOver(G))
        return; // why: WP-681 / D-24498 — block-all guard (Do-Over accept/decline choice)
    // why: block-all — pendingVictoryPileCardPick must be resolved before any other action (D-24067)
    if (hasPendingVictoryPileCardPick(G))
        return;
    // why: block-all — pendingDrawOrEmpowered must be resolved before any other action (D-24069)
    if (hasPendingDrawOrEmpowered(G))
        return;
    // why: block-all guard (WP-719 / D-24541) — a pending Covering Fire choice freezes the board.
    if (hasPendingCoveringFireChoice(G))
        return;
    // why: block-all guard (WP-795 / D-24664) — a pending Spinning Cyclone move-a-Villain choice freezes the board.
    if (hasPendingMoveVillainChoice(G))
        return;
    // why: block-all guard (WP-724 / D-24546) — a pending split-face "choose a side" freezes the board.
    if (hasPendingSplitFaceChoice(G))
        return;
    if (hasPendingCountScaledChoice(G))
        return;
    // why: block-all — pendingUndercoverChoice must be resolved before any other action (WP-678 / D-24494)
    if (hasPendingUndercoverChoice(G))
        return;
    // why: block-all — pendingReturnZeroCostDiscard must be resolved before any other action (D-24139)
    if (hasPendingReturnZeroCostDiscard(G))
        return;
    // why: block-all — pendingDiscardToPlay must be resolved before any other action (WP-383 / D-24184)
    if (hasPendingDiscardToPlay(G))
        return;
    // why: block-all — pendingReturnOnDiscard must be resolved before any other action (WP-498 / D-24301)
    if (hasPendingReturnOnDiscard(G))
        return;
    // why: block-all — pendingGiveHqHeroChoice (Paibok Fight) must be resolved first (WP-532 / D-24343)
    if (hasPendingGiveHqHeroChoice(G))
        return;
    // why: block-all — pendingCopyPowersChoice (Rogue's Copy Powers) must be resolved first (WP-535 / D-24345)
    if (hasPendingCopyPowersChoice(G))
        return;
    if (hasPendingSeatChoice(G))
        return; // why: WP-684 / D-24501 — block-all (non-active/multi-seat pending choice)
    // why: D-24180 — a player who used the Wound Healing ability this turn may not
    // fight or recruit for the rest of the turn (the reverse lock).
    if (hasHealedThisTurn(G))
        return;
    // Step 3: Mutate G — reuse the shared villain defeat-core (WP-486 / D-24291),
    // then spend attack + mark acted. Both are EXCLUDED from the shared core and
    // stay here in the fight move: Silent Sniper's defeat spends no attack and is a
    // card play, not a fight, so its onPlay handler must not spend attack or set the
    // acted-this-turn flag. No onFight villain ability reads G.turnEconomy.attack or
    // G.hasActedThisTurn, so running the fight debit + hasActedThisTurn AFTER the core
    // is byte-identical to the prior inline order — the unmodified fightVillain tests
    // are the oracle. The core's turnEconomy writes are woundsDrawn, Hand Ninjas'
    // Fight Recruit grant (D-24350) and, WP-819 / D-24753, Baron Zemo's paid rescue,
    // which spends 2 Recruit inside the core before the Fight effects; none of them
    // touches attack, and the rescue was judged against the simulated fight debit.
    // why: WP-817 / D-24750 — pay the fight discard cost first ("to fight Primus, you must
    // also discard …"), so the log reads discard then defeat. Only this move pays it; the
    // shared defeat core (Silent Sniper, defeat choices) never does. Step 1 already proved
    // discardCardId is a string in hand whenever a cost exists.
    if (fightDiscardCost !== null && typeof discardCardId === 'string') {
        discardFromHand(G, ctx.currentPlayer, discardCardId);
        pushLog(G, `Player ${ctx.currentPlayer} discarded ${formatCardRef(G.cardDisplayData, discardCardId)} to fight ${formatCardRef(G.cardDisplayData, cardId)}.`, 'applied');
    }
    // why: WP-819 / D-24753 — Baron Zemo's rescue is decided HERE, after every silent return
    // above (so a declined-request line is only ever logged for a fight that happens), after
    // WP-817's fight discard and its line (the discard never touches the economy), and before
    // the core, which only executes the decision. Its declined lines precede the fight's lines.
    const isRescuePaid = decideRescueOnFight(G, ctx.currentPlayer, payToRescueBystander, useExcessiveViolence, requiredFightCost, fightTarget);
    // why: D-24802 — a Size-Changing note ONLY when the discount changed the price, logged just
    // before the core's "fought" line, so every full-price fight's log is byte-identical.
    if (fightPrice !== villainAttack) {
        pushLog(G, buildFightSizeChangingLine(ctx.currentPlayer, formatCardRef(G.cardDisplayData, cardId), villainAttack, fightPrice), 'applied');
    }
    // why: WP-819 / D-24753 — only this move passes the rescue option ("Whenever you
    // fight"); the free defeats (Silent Sniper, Cruel Ruler) call the core without it.
    defeatCityVillainCore(G, ctx, cityIndex, { random }, { payRescueOnFight: isRescuePaid });
    // why: WP-736 / D-24556 / D-24557 — Excessive Violence opt-in overspend: the caller asked for
    // it AND one extra [attack] beyond the fight cost is affordable AND EV has not already been used
    // this turn (once per turn). A false result fights normally (validation-phase silent decline;
    // moves never throw). The core may have changed the Recruit side of the economy (Baron Zemo's
    // paid rescue spends 2 Recruit, WP-819 / D-24753; a Fight effect may grant Recruit, Hand Ninjas,
    // D-24350) but never attack, and the rescue was paid only when 2 Recruit remained after a
    // simulated debit that already included the open EV +1. So the spendable figure read here
    // still decides EV exactly as the pre-core economy would.
    const isExcessiveViolenceActive = useExcessiveViolence === true &&
        getSpendableAttackForTarget(G.turnEconomy, fightTarget) >= requiredFightCost + 1 &&
        G.turnEconomy.excessiveViolenceUsedThisTurn !== true;
    // why: WP-580 / D-24389 + WP-790 / D-24652 — spendFightCostForTarget debits eligible
    // restricted attack first (narrowest-first), then plain attack, then unspent recruit when the
    // conversion is active; identical to spendAttack when no grant and no conversion exist.
    // why: WP-736 / D-24557 — a SINGLE debit of requiredFightCost + the EV extra (0 or 1); never a
    // double-spend. Fire STRICTLY after this debit (RS-1) so a razor-teeth recruit grant cannot
    // change what the spend pulls under a recruit-as-attack loadout.
    G.turnEconomy = spendFightCostForTarget(G.turnEconomy, requiredFightCost + (isExcessiveViolenceActive ? 1 : 0), fightTarget);
    // why: D-24802 — rules v23 p.30: "if you fight a Villain with Microscopic Size-Changing and
    // reduce its value to a negative number, you gain that many [Attack] when you fight it!"
    // Added after the debit, so the gain can never pay for this same fight.
    if (fightPrice < 0) {
        G.turnEconomy = addResources(G.turnEconomy, -fightPrice, 0);
    }
    if (isExcessiveViolenceActive) {
        // why: WP-736 / D-24556 — close the once-per-turn window, then fire every enrolled EV ability
        // in enrolment (play) order via the fight-time driver (after the defeat + the extra-attack debit).
        G.turnEconomy = markExcessiveViolenceUsed(G.turnEconomy);
        fireExcessiveViolencePlays(G, context, ctx.currentPlayer);
    }
    // why: D-24180 — this successful fight marks the player as having acted this
    // turn, which bars the Wound Healing ability for the rest of the turn.
    G.hasActedThisTurn = true;
    // why: WP-656 / D-24467 — signal this City Villain defeat for Diamond Form's
    // wait-and-see grant ("Whenever you defeat a Villain or Mastermind this turn").
    // GATED on a pending deferred grant so the flag never enters G for a game with no
    // such grant (oracle-safety, D-24377 §6). It is EDGE-triggered: resolveDeferredHeroGrants
    // consumes it after this move, so the grant fires once per defeat, never per later
    // move. Placed at the fight-move tail (beside hasActedThisTurn), NOT the shared
    // defeatCityVillainCore — matching the WP's "fightVillain success site" scope, so
    // Silent Sniper's free defeat (which reuses the core) is deliberately out of scope.
    // why (henchmen count, D-24603): fightVillain defeats both villains and henchmen, and
    // BOTH satisfy "defeat a Villain" — Universal Rules v23 §"Henchmen Are
    // Villains/Adversaries": "Henchman Villain cards are indeed Villains." So every
    // successful fightVillain signals, with no card-type gate. (D-24467's #2030 henchman
    // exclusion rested on the opposite premise and is reversed.)
    if (G.deferredConditionalGrants !== undefined &&
        G.deferredConditionalGrants.length > 0) {
        G.villainOrMastermindDefeatedSinceResolve = true;
    }
    // why: WP-830 / D-24763 — The Unbreakable Enigma Code: "Whenever you fight a Villain, you may pay
    // 1 [recruit] to look at one of the face-down Enigma Cards." Only this move fights (the free
    // defeats never offer it); the offer opens after the move. Absent outside the scheme.
    if (G.enigmaCode !== undefined) {
        queueEnigmaPeekOffer(G, ctx.currentPlayer);
    }
}
/**
 * Builds the log line for a fight whose price Size-Changing changed (D-24802).
 *
 * @param playerId - The fighting player.
 * @param villainRef - The fought villain's formatted card reference.
 * @param villainAttack - The villain's Attack before Size-Changing.
 * @param fightPrice - The Size-Changing price; negative when Microscopic went below 0.
 * @returns The log line.
 */
export function buildFightSizeChangingLine(playerId, villainRef, villainAttack, fightPrice) {
    if (fightPrice < 0) {
        return `Size-Changing: Player ${playerId} fights ${villainRef} (${String(villainAttack)} Attack) for 0 and gains ${String(-fightPrice)} Attack.`;
    }
    return `Size-Changing: Player ${playerId} fights ${villainRef} (${String(villainAttack)} Attack) for ${String(fightPrice)}.`;
}
/**
 * Decides whether Baron Zemo's rescue on a fight is paid (WP-819 / D-24753),
 * and logs why when a requested rescue is declined.
 *
 * Called by fightVillain after every validation and gate (so the fight is
 * certain) and before defeatCityVillainCore. Apart from the declined-request
 * log line it reads G only; the core executes the decision.
 *
 * @param G - Game state (a declined request appends one log line).
 * @param currentPlayer - The fighting player.
 * @param payToRescueBystander - The move's opt-in argument.
 * @param useExcessiveViolence - The move's Excessive Violence argument.
 * @param requiredFightCost - The fight cost the move will debit (cost + Patrol).
 * @param fightTarget - The fought City space.
 * @returns True when the core should pay the rescue.
 */
function decideRescueOnFight(G, currentPlayer, payToRescueBystander, useExcessiveViolence, requiredFightCost, fightTarget) {
    // why: no Zemo, or no request → ignored with no log line, so every other match is byte-identical.
    const isRescueRequested = payToRescueBystander === true && isRescueOnFightOffered(G);
    if (!isRescueRequested) {
        return false;
    }
    // why: under the WP-580 conversion the fight itself can spend Recruit, so the rescue is judged
    // against a simulated fight debit that includes an open Excessive Violence +1; the rescue must
    // never overdraw the fight or EV.
    const isExcessiveViolenceOpen = useExcessiveViolence === true && G.turnEconomy.excessiveViolenceUsedThisTurn !== true;
    const fightDebit = requiredFightCost + (isExcessiveViolenceOpen ? 1 : 0);
    const isSupplyEmpty = G.piles.bystanders.length === 0;
    const isRescuePaid = !isSupplyEmpty && canPayRescueOnFight(G.turnEconomy, fightDebit, fightTarget);
    if (isRescuePaid) {
        return true;
    }
    if (isSupplyEmpty) {
        pushLog(G, `Player ${currentPlayer} could not rescue a Bystander — the Bystander supply is empty (Baron Zemo).`, 'blocked');
    }
    else {
        pushLog(G, `Player ${currentPlayer} could not pay ${RESCUE_ON_FIGHT_RECRUIT_COST} Recruit to rescue a Bystander after the fight cost (Baron Zemo).`, 'blocked');
    }
    return false;
}
/**
 * Pays Baron Zemo's rescue inside the defeat core (WP-819 / D-24753): spends 2
 * Recruit and moves the Bystander supply's top card to the fighter's Victory Pile.
 *
 * Writes a fresh `zones.victory` array and never touches the attached-Bystander
 * award, so the fightResolved event's `bystandersRescued` keeps counting only the
 * fought Villain's own Bystanders.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param currentPlayer - The fighting player.
 * @param cardId - The fought Villain, named in the log line.
 */
function applyRescueOnFight(G, currentPlayer, cardId) {
    // why: top-of-pile convention — bystanders[0] is the next supply Bystander (D-21501). The move
    // already checked the supply; an empty one here is a defensive no-op that spends nothing.
    const topBystander = G.piles.bystanders[0];
    if (topBystander === undefined) {
        return;
    }
    G.turnEconomy = spendRecruit(G.turnEconomy, RESCUE_ON_FIGHT_RECRUIT_COST);
    const playerZones = G.playerZones[currentPlayer];
    const moveResult = moveCardFromZone(G.piles.bystanders, playerZones.victory, topBystander);
    G.piles.bystanders = moveResult.from;
    playerZones.victory = moveResult.to;
    pushLog(G, `Player ${currentPlayer} paid ${RESCUE_ON_FIGHT_RECRUIT_COST} Recruit to rescue a Bystander on fighting ${formatCardRef(G.cardDisplayData, cardId)} (Baron Zemo).`, 'applied');
}
/**
 * Shared villain defeat-core (WP-486 / D-24291).
 *
 * Removes the villain/henchman at `cityIndex` from the City into the current
 * player's victory pile, awards its attached Bystanders and captured HQ Heroes,
 * fires the villain's `onFight` abilities, and emits the log lines + the
 * fightResolved notable event — the exact Step-3+ body fightVillain formerly
 * inlined, MINUS `spendAttack` and `G.hasActedThisTurn` (those stay in the fight
 * moves; see fightVillain for why the exclusion is byte-identical). Reused by
 * fightVillain (the normal fight) and by Silent Sniper's `defeat-with-bystander`
 * hero effect / its resolveDefeatChoice move (the free defeat, no attack spend).
 *
 * WP-819 / D-24753: when fightVillain passes `options.payRescueOnFight`, the core
 * also pays Baron Zemo's rescue (spends 2 Recruit and moves the supply's top
 * Bystander to the Victory Pile) after the attached awards and before the
 * Villain's Fight effects. Besides a Recruit-granting Fight effect (Hand Ninjas),
 * that Recruit spend is the core's only write to the attack/recruit spendable.
 *
 * why (WP-486): a documented internal invocation of the existing defeat path —
 * the onFight/onDefeat hook execution and Bystander/hero award live in exactly
 * one place, so the free defeat can never drift from the paid fight.
 *
 * The caller guarantees `cityIndex` holds a villain; a null/undefined space is a
 * silent no-op (moves never throw). The defeating player is `ctx.currentPlayer`.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - The bare boardgame.io ctx (currentPlayer + turn), typed unknown to
 *   avoid a framework import; forwarded to executeVillainAbilities.
 * @param cityIndex - The City space index of the villain to defeat.
 * @param shuffleContext - ShuffleProvider ({ random }) for a Fight scry reshuffle.
 * @param options - Optional. `payRescueOnFight: true` pays Baron Zemo's rescue
 *   (WP-819 / D-24753); only fightVillain passes it, after deciding it is affordable.
 */
export function defeatCityVillainCore(G, ctx, cityIndex, shuffleContext, options) {
    // why: mirrors executeVillainAbilities — narrow the unknown ctx to the one
    // field this core reads (the defeating player), no framework import.
    const currentPlayer = ctx.currentPlayer;
    const cardId = G.city[cityIndex];
    // why: defense-in-depth — the caller (fightVillain gate / hero eligibility
    // builder) guarantees an occupied space, but an empty space is a silent no-op
    // rather than a throw (moves never throw).
    if (cardId === null || cardId === undefined) {
        return;
    }
    // why: MVP has no attack point check; WP-018 adds the economy. Any player
    // can fight any occupied City space without spending attack points.
    G.city[cityIndex] = null;
    // why: WP-514 / D-24327 — Secret Invasion "If you defeat that Hero, you gain it":
    // a defeated Skrull (a Hero acting as a villain) routes to the defeating player's
    // DISCARD instead of the victory pile, and its conversion overlay is cleared (it is
    // a Hero again in the discard). Guarded on the 'skrull' origin — every non-Skrull
    // defeat routes to victory unchanged (tested both ways). The fought cardId is pushed
    // DIRECTLY here (awardAttachedHeroes below handles a villain's ATTACHED heroes, not
    // the fought card). A gain log line follows so a Hero the player never recruited does
    // not appear in their deck with no trail (mirrors the attached-hero log below).
    // why: WP-518 / D-24331 — capture the Skrull origin BEFORE the delete below so
    // the fightResolved narrative (composed after the origin overlay is cleared) can
    // announce the gain in the center-screen overlay, not only in the durable log.
    let skrullGained = false;
    let officerGained = false;
    if (G.convertedVillainOrigins?.[cardId] === 'skrull') {
        skrullGained = true;
        G.playerZones[currentPlayer].discard.push(cardId);
        delete G.convertedVillainOrigins[cardId];
        pushLog(G, `Player ${currentPlayer} defeated the Skrull ${formatCardRef(G.cardDisplayData, cardId)} and gained the Hero into their discard pile.`);
    }
    else if (G.convertedVillainOrigins?.[cardId] === 'shield-officer') {
        // why: WP-823 / D-24756 — Brainwash the Military "When you defeat a S.H.I.E.L.D.
        // Officer, gain it as a Hero." Every Officer is identical, and the engine holds an
        // Officer Hero as the fungible SHIELD_OFFICER_EXT_ID token, which already carries
        // its stats, team, display and the invariant exemption. So the instance leaves play
        // and the token goes to the defeater's discard. It is the same card, so nothing is
        // drawn from G.piles.officers, and it never enters the Victory Pile (it scores as no
        // Villain or Bystander, and Savior never counts it).
        officerGained = true;
        G.playerZones[currentPlayer].discard.push(SHIELD_OFFICER_EXT_ID);
        delete G.convertedVillainOrigins[cardId];
        pushLog(G, `Player ${currentPlayer} defeated ${formatCardRef(G.cardDisplayData, cardId)} and gained it as a Hero into their discard pile.`);
    }
    else {
        G.playerZones[currentPlayer].victory.push(cardId);
    }
    // Step 3b: Award attached bystanders to player's victory zone (WP-017)
    const victoryBefore = G.playerZones[currentPlayer].victory.length;
    const awardResult = awardAttachedBystanders(cardId, G.attachedBystanders, G.playerZones[currentPlayer].victory);
    G.attachedBystanders = awardResult.attachedBystanders;
    G.playerZones[currentPlayer].victory = awardResult.playerVictory;
    // Step 3c: Award attached heroes to player's discard pile (WP-214)
    // why: WP-431 — capture the attached-hero list BEFORE awardAttachedHeroes
    // deletes the mapping entry, so the log below can name each hero returned to
    // the defeating player's discard ("Fight: Gain that Hero"). Deterministic
    // append order; empty when the villain captured no HQ hero.
    const heroesReturnedToDiscard = [...(G.villainAttachedHeroes?.[cardId] ?? [])];
    awardAttachedHeroes(G, cardId, currentPlayer);
    // why: WP-819 / D-24753 — Baron Zemo's paid rescue resolves BEFORE the Villain's Fight
    // effects. Rules v23 L772-792 let the player do the Fight and Rescue effects "in any order";
    // rescuing first is player-optimal, because the Masters of Evil (WWII) Savior-gated Fight
    // lines then count the rescued Bystander. The option defaults off, so the free defeats that
    // reuse this core never pay.
    if (options?.payRescueOnFight === true) {
        applyRescueOnFight(G, currentPlayer, cardId);
    }
    // why: Fight: effects fire after the bystander award (Step 3b) and after
    // the "fought" line, so they observe post-award pile state. A Fight:
    // captureBystander awards the newly attached bystander immediately (the card
    // is already in the victory pile), avoiding a stranded bystander (WP-185).
    // WP-316: capture the executor's per-effect results (keywords + targets) for
    // the fightResolved emission and the effect-narration log line below.
    // why: WP-478 / D-24285 — pass the caller's `random` (wrapped as a ShuffleProvider
    // `{ random }`) so a Fight scry (Doombot Legion) can reshuffle the current
    // player's discard when their deck is short.
    // why: WP-489 / D-24295 — pass the fought `cityIndex` (a plain number captured
    // before the slot was nulled) so a location-gated Fight ability (Abomination /
    // the Lizard) can check which named City space the villain was fought on.
    // why: the "fought" line goes in BEFORE the Fight effects fire, because some
    // handlers log their own line (Viper's "gained a Wound", match SiJuKfJ0zGf 9.2.11),
    // which otherwise appeared before the fight that caused it.
    pushLog(G, `Player ${currentPlayer} fought ${formatCardRef(G.cardDisplayData, cardId)} at city space ${cityIndex}.`);
    const appliedFightResults = executeVillainAbilities(G, ctx, cardId, 'onFight', shuffleContext, cityIndex);
    // why: WP-316 — map results→keywords so the fightResolved `appliedEffects`
    // field and the composeFightNarrative string stay byte-identical to main; the
    // hashed notableEvents surface (and the arena-client) never observe the
    // per-target widening, so finalStateHash is unchanged.
    const appliedFightEffects = appliedFightResults.map((result) => result.keyword);
    const bystandersRescued = awardResult.playerVictory.length - victoryBefore;
    if (awardResult.playerVictory.length > victoryBefore) {
        pushLog(G, `Player ${currentPlayer} rescued ${bystandersRescued} bystander(s) from ${formatCardRef(G.cardDisplayData, cardId)}.`);
    }
    // why: WP-431 — narrate the captured HQ hero(es) returning to the defeating
    // player's discard on villain defeat (awardAttachedHeroes above; the villain's
    // "Fight: Gain that Hero" text). Without this line a hero the player never
    // recruited appears in their deck with no log trail. Durable log (G.messages,
    // hash-excluded per D-24081). One line per hero; none when no hero was captured.
    for (const heroId of heroesReturnedToDiscard) {
        pushLog(G, `Player ${currentPlayer} gained ${formatCardRef(G.cardDisplayData, heroId)} from ${formatCardRef(G.cardDisplayData, cardId)} into their discard pile.`);
    }
    // why: WP-316 + WP-319 — resolve the effect targets to display names ONCE (via
    // G.cardDisplayData; the composer stays pure). The same resolved results feed
    // BOTH the durable `Fight effect:` log line below AND the fightResolved
    // narrative (so the center-screen overlay and the log name the same hero).
    const resolvedFightResults = resolveEffectResultNames(G, appliedFightResults);
    // why: WP-316 — narrate the Fight: effect targets into the durable log
    // (G.messages, hash-excluded per D-24081), after the fought/rescued pushes and
    // before the fightResolved event push. Length-guarded: no line when no effect
    // applied (an effectless fight pushes no effect line).
    if (appliedFightResults.length > 0) {
        pushLog(G, `Fight effect: ${composeEffectResultLogLine(resolvedFightResults)}.`);
    }
    // why: WP-200 — emission is the LAST step in this core (after Fight:
    // effect dispatch AND after both `G.messages.push` calls), so the event
    // observes the fully settled post-mutation state. `bystandersRescued`
    // reflects the post-award delta; `appliedEffects` is the executor's
    // dispatch-order return (post-safe-skip). Narrative composition uses the
    // resolved display name with a defensive fallback to raw cardId when
    // G.cardDisplayData has no matching entry — emissions never throw
    // (D-20006 + WP §Non-Negotiable Constraints).
    // why: WP-200 — defensive access; legacy test G states may leave
    // `cardDisplayData` undefined. Production setup always builds it.
    const fightCardDisplay = G.cardDisplayData?.[cardId];
    const fightCardName = fightCardDisplay && typeof fightCardDisplay.name === 'string' && fightCardDisplay.name.length > 0
        ? fightCardDisplay.name
        : cardId;
    G.notableEvents.push({
        type: 'fightResolved',
        playerId: currentPlayer,
        cardId,
        citySpace: cityIndex,
        bystandersRescued,
        appliedEffects: appliedFightEffects,
        // why: WP-319 — the narrative now names the specific effect target(s) (the
        // KO'd hero, captured HQ hero, etc.) via the resolved results, so the
        // center-screen NotableEventOverlay announces the hero. `appliedEffects`
        // above stays the keyword array (byte-identical) for the overlay's badges.
        narrative: composeFightNarrative(fightCardName, bystandersRescued, resolvedFightResults, 
        // why: WP-518 / D-24331 — announce the Secret Invasion Skrull gain in the
        // overlay; false for every ordinary villain defeat (byte-identical narrative).
        // WP-823 / D-24756: a Brainwash the Military Officer gained as a Hero shares the
        // same "gained the Hero" clause.
        skrullGained || officerGained),
    });
    // why: WP-542 / D-24351 — Endless Armies of HYDRA's "Fight: Play the top two cards of
    // the Villain Deck." The executor's play-villain-deck-cards handler is a no-op (the move
    // reaches the reveal pipeline, the executor does not), so the actual play fires HERE,
    // AFTER the defeat is fully settled (victory pile, awards, fightResolved event) so the
    // defeat is logged/emitted first and the played cards' reveals follow as its consequence.
    // The move does not receive a RevealContext or an implementationMap, so build the narrow
    // context performVillainReveal reads from the fought ctx + shuffle source and import the
    // STATIC DEFAULT_IMPLEMENTATION_MAP. Each played card resolves as a normal reveal,
    // recursing through the rule pipeline; the both-empty guard terminates the loop.
    // why: WP-837 / D-24770 — the current player decides a reveal-gated play.
    const fightPlayCount = villainCardPlaysVillainDeckCards(G, cardId, 'onFight', currentPlayer);
    if (fightPlayCount > 0) {
        // why: narrate the fight-triggered plays BEFORE the reveals so the log
        // attributes them to this villain. Without it the played cards' consequences
        // (a city reveal, a captured bystander, or — as with Endless Armies feeding a
        // Red Skull Master Strike — a strike) appear with no cause, unlike the scheme
        // twist path which self-narrates "playing the next villain-deck card."
        pushLog(G, `Fight effect: ${formatCardRef(G.cardDisplayData, cardId)} plays the top ${fightPlayCount} card(s) of the Villain Deck.`);
        const revealContext = {
            random: shuffleContext.random,
            // why: D-24704 — forward the framework turn so the played cards' villain
            // traces carry it, as this Fight's own onFight traces already do.
            ctx: { currentPlayer, turn: readTurnNumber(ctx) },
        };
        playTopVillainDeckCards(G, revealContext, DEFAULT_IMPLEMENTATION_MAP, fightPlayCount);
    }
}
//# sourceMappingURL=fightVillain.js.map